import { CustomFontSize } from '../components/DialogHeader/DialogHeader';
import { ButtonSkin, Size } from '@wix/design-system';
import nautilusGlyph from '../assets/images/nautilus-glyph.svg?url';
// "Symphony by wix" brand lockup (monochrome charcoal), from the MKT Figma
import symphonyLogo from '../assets/images/symphony-logo.svg?url';
// vibu "potato" mark
import vibuLogo from '../assets/images/vibu-logo.svg?url';

type Skin = 'light' | 'dark' | 'standard'; // 'light' should be used on dark backgrounds
export type LogoPosition = 'left' | 'center';

type ButtonDesign = {
  skin: ButtonSkin;
  textKey?: string;
  color?: string;
  fullWidth?: boolean;
  height?: string;
  fontSize?: string;
  showArrowOnButton?: boolean;
  hideSuffixIcon?: boolean; // Hide the ChevronRight suffix icon on EmailStep button (default: false, shows icon for horizontal layouts)
  // Label color (enabled/hover/pressed/focus). The WDS button reads the SHARED
  // token --wds-color-text-standard-primary-light (used across ~130 components),
  // so this is applied INLINE-scoped to the button (see ButtonWithLoader) rather
  // than as a global cssVariable.
  textColor?: string;
  // Disabled-state text color. The disabled fill is set via the button-only
  // token --wds-color-fill-standard-primary-disabled (cssVariables), but the
  // disabled text token (--wds-color-text-standard-primary-light) is the same
  // shared token, so it's also applied INLINE-scoped to the button.
  disabledTextColor?: string;
  // In-button spinner colors (WDS Loader inside ButtonWithLoader). Applied
  // inline on the button so they don't affect the full-page loader, which uses
  // the same --wds-loader-* vars (see loaderColors.fullPage).
  loaderColors?: {
    fill?: string; // spinner track (--wds-loader-fill)
    fillLoading?: string; // spinner arc (--wds-loader-fill-loading)
  };
};

export type SocialButtonDesign = {
  height?: string;
  borderColor?: string;
  borderWidth?: string;
  borderRadius?: string;
  backgroundColor?: string;
  textColor?: string; // Label color (default WDS #000624)
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
  activeBackgroundColor?: string;
  activeBorderColor?: string; // Pressed-state border (WDS :active sets no border)
  disabledBackgroundColor?: string; // Disabled fill (default is a hardcoded tint)
  disabledTextColor?: string; // Disabled label color
  errorBorderColor?: string; // Border when the provider returned an error
  loaderFill?: string; // In-button spinner track (--wds-loader-fill)
  loaderFillLoading?: string; // In-button spinner arc (--wds-loader-fill-loading)
  focusBackgroundColor?: string;
  focusOutline?: string;
  focusOutlineOffset?: string;
  iconTextLayout?: 'centered' | 'space-between';
  fontSize?: string;
};

export type SwitchLinkDesign = {
  color?: string; // Regular link color
  hoverColor?: string; // Hover state color
  focusRingColor?: string; // Focus ring outline color
  underline?: 'none' | 'always' | 'onHover'; // Underline behavior
  fontWeight?: string; // Font weight (e.g., '400', '500', '600')
};

export type DividerDesign = {
  textColor?: string; // Color for "or" text (default: #20303c)
  lineColor?: string; // Color for divider lines (default: #e0e5eb)
};

export type LoaderColors = {
  fill?: string; // Background arc color (--wds-loader-fill)
  fillLoading?: string; // Spinning arc color (--wds-loader-fill-loading)
};

type FormSpacing = {
  inputButtonGap?: string; // Gap between input and button when no forgot email link (CSS value, e.g. '12px', '24px', default '24px')
  inputForgotEmailGap?: string; // Gap between input and forgot email link when present (CSS value, e.g. '8px', '12px', defaults to inputButtonGap)
  forgotEmailButtonGap?: string; // Gap between forgot email link and button (CSS value, e.g. '12px', '24px', default '24px')
  policiesDividerSpacing?: {
    top?: string; // Spacing above horizontal divider before policies (CSS value, e.g. '32px', '44px', default '32px')
    bottom?: string; // Spacing below horizontal divider before policies text (CSS value, e.g. '24px', '44px', default '24px'). Ignored when centerVertically is true.
    centerVertically?: boolean; // When true, vertically centers the policies text between divider and card bottom (default: false)
  };
  titleSectionGap?: string; // Gap between title and subtitle in DialogHeader (CSS value, default: '3px' for vertical, 6px for horizontal)
  forgotEmailAlignment?: 'left' | 'center'; // Alignment of Forgot Email link in EmailStep (default: 'left' for horizontal, 'center' for vertical)
  enterCodeButtonLayout?: 'stacked' | 'horizontal'; // Button layout in EnterCode 2FA dialogs: 'stacked' = vertical full-width buttons (Verify on top, Resend below), 'horizontal' = side-by-side (default)
  // Total visual gap between input and button in secondary dialogs (e.g., SSO enter email)
  // MINIMUM: 15px - values below this are clamped to 15px (due to WDS InputWithLabel internal padding)
  secondaryDialogInputButtonGap?: {
    desktop?: string; // Desktop total gap (CSS value, e.g. '45px', default: '27px', min: '15px')
    mobile?: string; // Mobile total gap (CSS value, e.g. '45px', default: '27px', min: '15px')
  };
};

type Title = {
  key: string;
  font?: string;
  size?: Size;
};

type Container = {
  primary: {
    padding: string;
  };
  secondary: {
    padding: string;
  };
  borderRadius: string;
  boxShadow: string;
  fullScreen?: boolean; // When true, removes card styling and fills screen (for mobile full-screen dialogs)
};

type AuthDialog = {
  title?: Title;
  subtitle?: Title;
  flows?: {
    id: string;
    title: Title;
    subtitle?: Title;
  }[];
  primaryButtonDesign?: ButtonDesign;
  secondaryButtonDesign?: ButtonDesign;
  emailLabelKey?: string;
  dividerTextKey?: string; // Translation key for "or" divider text between social buttons and email input (vertical layout)
};

type SignupAuthDialog = AuthDialog & {
  emailFormButtonTextKey?: string; // Translation key for button in the email form step (with password fields)
};

type LoginAuthDialog = AuthDialog & {
  alreadyHaveAccount?: {
    title?: Title;
    subtitle?: Title;
  };
};

export const SocialProviders = {
  GOOGLE: 'google',
  FACEBOOK: 'facebook',
  APPLE: 'apple',
};

export const PRESETS = {
  DAYFUL: 'dayful',
  EDITORX: 'editorx',
  HOPP: 'hopp',
  EDUCATION_STUDENT: 'EDUCATION_STUDENT',
  EDUCATION_TEACHER: 'EDUCATION_TEACHER',
  CODUX: 'codux', // New branding for WixComponentsStudio (WCS)
  LOGO_MAKER: 'logo_maker',
  BUSINESS_CARD_MAKER: 'bcm',
  WIXEL: 'wixel',
  NAUTILUS: 'nautilus',
  PICASSO: 'picasso',
  WIX_2025: 'wix-2025',
  SYMPHONY: 'symphony',
  VIBU: 'vibu',
};

export type LogoConfig = Partial<{
  // Default - wix logo
  src: string;
  position: LogoPosition; // Default - 'left'
  height: string; // Default - 24px
  mobile: {
    height: string; // Default - 20px
  };
  alt: string; // Default - 'Wix'
  url: string; // Default - www.wix.com
  dividerColor: string; // Color for vertical divider between main and secondary logo (SSO screens)
}>;

export type ViewPreset = Partial<{
  id: string; // Default - preset key
  nameKey: string; // Default - 'presets.names.wix' = Wix
  skin: Skin; // Default - standard
  shouldDisablePostLoginSuggestions: boolean; // Default - false
  container: {
    layout: 'vertical' | 'horizontal'; // Default - horizontal
    desktop: Container;
    mobile: Container;
  };
  background: string;
  // Optional CSS class (in App.scss) applied to the app root for the page
  // background, as an alternative to the inline `background` string. Used when
  // the background references bundled image assets (kept out of the JS graph).
  backgroundClassName: string;
  login: LoginAuthDialog;
  signup: SignupAuthDialog;
  landingPage: AuthDialog; // Main landing page (email step)
  logo: LogoConfig;
  partners: Record<string, Partial<Omit<ViewPreset, 'partners'>>>;
  policies: {
    policyKey?: string; // Default - 'policies_section.signup_content'
    termsOfUseUrl?: string; // Default - wix terms of use
    privacyPolicyUrl?: string; // Default - wix privacy policy
    linkColor?: string; // Color for policy links (default: inherit)
    // Let the Minimal policies (Terms/Privacy links) size to their content
    // instead of the default 400px min-width. In the narrow vertical wrapper the
    // 400px block overflows and pushes the links off-center; fitting to content
    // lets them center. Opt-in per preset (default: false → legacy 400px).
    minimalFitContent?: boolean;
  };
  buttonDesign?: ButtonDesign;
  // Header back button ("‹ Back"). Consumed in MainHeader.
  backButtonDesign?: {
    chevronIcon?: boolean; // Use a chevron (‹) instead of the default arrow (←)
    fontWeight?: string; // Label weight (e.g. '600' for semibold)
    fontSize?: string; // Desktop label size (e.g. '16px')
    mobileFontSize?: string; // Mobile label size (e.g. '14px')
  };
  socialButtonDesign?: SocialButtonDesign;
  // Values here override socialButtonDesign when new Google SDK FT is enabled
  newGoogleSdkSocialButtonDesign?: SocialButtonDesign;
  // Styling for dialog switch links (Sign Up, Log In, Continue with SSO)
  switchLinkDesign?: SwitchLinkDesign;
  // Layout mode: when true, the vertical-layout login and email-step screens
  // render social buttons ABOVE the email form (matching the signup screen /
  // the Symphony Figma) instead of below. Default - false.
  socialFirst?: boolean;
  // Styling for "or" divider between social buttons and email input
  dividerDesign?: DividerDesign;
  // Styling for error toast notifications (SectionHelper handles background and text colors)
  errorToastDesign?: {
    iconColor?: string; // Default - '#E62214'
  };
  loaderColors?: {
    fullPage?: LoaderColors;
  };
  // When true, shows a card-style full-page loader (e.g. wix-2025).
  showCardLoader?: boolean;
  formSpacing?: FormSpacing;
  // Social providers separated with comma (for example - 'google,facebook')
  // Default - all ('google,facebook','apple')
  socialProviders: string;
  titleCustomFontSize: {
    authPage: {
      desktop: CustomFontSize;
      mobile: CustomFontSize;
    };
    secondaryFlow: {
      desktop: CustomFontSize;
      mobile: CustomFontSize;
    };
  };
  // Custom font size / line-height for the preset subtitle (overrides the WDS
  // "small" size). Applied inline on the subtitle text in AuthPage.
  subtitleFontSize: {
    fontSize: string;
    lineHeight?: string;
    mobileFontSize?: string; // Overrides fontSize below the 810px breakpoint
    letterSpacing?: string; // e.g. '-0.01em' for -1%
  };
  cssVariables: Record<string, string>;
  useBrandTheme?: boolean; // Default - true. When false, skips WDS Brand theme to allow full CSS variable theming
  secondaryTextColor?: string; // Override WDS Brand theme's hardcoded secondary text color (#595D70 → custom)
  // Default - 'header' (back button in the header, below the logo). 'aboveDialog'
  // renders it above the dialog card instead.
  backButtonPosition?: 'header' | 'aboveDialog';
  mobileBackButtonConfig?: {
    position: 'header' | 'hidden'; // Where to show back button on mobile
    marginTop?: string; // Vertical spacing from logo
    marginStart?: string; // Horizontal margin
    iconTextGap?: string; // Gap between arrow icon and text
  };
  dialogVerticalPosition?: 'top' | 'centered'; // Default - 'top'. When 'centered', dialog is vertically centered with minimum top offset
  dialogMinTopOffset?: string; // Minimum distance from top when dialogVerticalPosition is 'centered' (CSS value, e.g. '100px')
}>;

export const skinToColor = {};
export const DEFAULT_LOGO_HEIGHT = '26px';
const COMMON_NEW_GOOGLE_SDK_SOCIAL_BUTTON_DESIGN: SocialButtonDesign = {
  borderColor: '#DADCE0',
  hoverBackgroundColor: '#4285f414',
  hoverBorderColor: '#D2E3FC',
  focusBackgroundColor: '#4285f414',
  focusOutline: '2px solid #00639B',
  focusOutlineOffset: '1px',
};

export const ViewPresets: {
  [key: string]: ViewPreset;
} = {
  [PRESETS.DAYFUL]: {
    nameKey: 'presets.names.dayful',
    landingPage: {
      title: {
        key: 'emailStep.dayful.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
      subtitle: {
        key: 'dayful_subtitle',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/dayful-logo.png',
      height: '34px',
      url: 'https://www.dayful.com/',
    },
  },
  [PRESETS.HOPP]: {
    nameKey: 'presets.names.hopp',
    landingPage: {
      title: {
        key: 'emailStep.hopp.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
      subtitle: {
        key: 'hopp_subtitle',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/Hopp%20by%20wix%20logo%20(1).svg',
      height: '22px',
      url: 'https://www.hopp.co/',
    },
    partners: {
      discount: {
        logo: {
          src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/discount-hopp-logo.svg',
          height: '36px',
          mobile: { height: '30px' },
        },
      },
    },
  },
  [PRESETS.NAUTILUS]: {
    nameKey: 'presets.names.nautilus',
    landingPage: {
      title: {
        key: 'emailStep.nautilus.title',
      },
    },
    logo: {
      src: nautilusGlyph,
      height: '24px',
      alt: 'Nautilus',
      url: 'https://manage.wix.com/nautilus-app/',
    },
    signup: {
      title: {
        key: 'signup.title',
      },
    },
  },
  [PRESETS.EDUCATION_STUDENT]: {
    id: PRESETS.EDUCATION_STUDENT,
    nameKey: 'presets.names.education',
    landingPage: {
      title: {
        key: 'emailStep.education.student.title',
      },
    },
    login: {
      subtitle: {
        key: 'login.education.subtitle',
      },
    },
    signup: {
      title: {
        key: 'signup.education.student.title',
      },
    },
    logo: {
      src: 'https://images-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/642436ff-70f5-4ebc-8fea-47fab46f7f73/1693247680916',
      url: 'https://www.wix.com/education',
    },
    policies: {
      termsOfUseUrl:
        'https://www.wix.com/tomorrow/classroom/platform/terms-of-use',
      privacyPolicyUrl:
        'https://www.wix.com/tomorrow/classroom/platform/privacy-policy',
    },
    socialProviders: SocialProviders.GOOGLE,
  },
  [PRESETS.EDUCATION_TEACHER]: {
    id: PRESETS.EDUCATION_TEACHER,
    nameKey: 'presets.names.education',
    landingPage: {
      title: {
        key: 'emailStep.education.teacher.title',
      },
    },
    login: {
      subtitle: {
        key: 'login.education.subtitle',
      },
    },
    signup: {
      title: {
        key: 'signup.education.teacher.title',
      },
    },
    logo: {
      src: 'https://images-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/642436ff-70f5-4ebc-8fea-47fab46f7f73/1693247680916',
      url: 'https://www.wix.com/education',
    },
    policies: {
      termsOfUseUrl:
        'https://www.wix.com/tomorrow/classroom/platform/terms-of-use',
      privacyPolicyUrl:
        'https://www.wix.com/tomorrow/classroom/platform/privacy-policy',
    },
    socialProviders: SocialProviders.GOOGLE,
  },
  [PRESETS.CODUX]: {
    logo: {
      src: 'https://wixplosives.github.io/codux-assets-storage/login/codux-logo.svg',
      position: 'center',
      height: '100px',
      mobile: {
        height: '80px',
      },
      url: 'https://www.codux.com/',
    },
    signup: {
      title: {
        key: 'signup.title',
      },
    },
    policies: {
      termsOfUseUrl: 'https://www.wix.com/about/terms-of-use',
      privacyPolicyUrl: 'https://www.wix.com/about/privacy',
    },
  },
  [PRESETS.EDITORX]: {
    nameKey: 'presets.names.editorx',
    landingPage: {
      title: {
        key: 'emailStep.editorx.title',
      },
    },
    signup: {
      title: {
        key: 'signup.title',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/EditorX_logo_36x168_black.svg',
      url: 'https://www.editorx.com/',
    },
  },
  [PRESETS.LOGO_MAKER]: {
    nameKey: 'presets.names.logo_maker',
    logo: {
      url: 'https://www.wix.com/logo/maker/',
    },
    landingPage: {
      title: {
        key: 'emailStep.logo_maker.title',
      },
    },
    login: {
      title: {
        key: 'login.logo_maker.title',
      },
    },
    signup: {
      title: {
        key: 'signup.logo_maker.title',
      },
    },
  },
  [PRESETS.BUSINESS_CARD_MAKER]: {
    nameKey: 'presets.names.business_card_maker',
    logo: {
      url: 'https://www.wix.com/tools/business-card-maker/',
    },
    login: {
      title: {
        key: 'login.business_card_maker.title',
      },
    },
    signup: {
      title: {
        key: 'signup.business_card_maker.title',
      },
    },
  },

  [PRESETS.WIXEL]: {
    id: PRESETS.WIXEL,
    shouldDisablePostLoginSuggestions: true,
    nameKey: 'presets.names.wixel',
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 52px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wixel-logo.svg',
      url: 'https://www.wix.com/wixel',
      height: '28px',
      alt: 'Wixel',
      mobile: {
        height: '24px',
      },
    },
    landingPage: {
      title: {
        // `emailStep.wixel.title` was never added to Babel; reuse the existing,
        // fully-translated branded signup title ("Get started with Wixel") so the
        // landing screen shows branded copy instead of a raw key.
        key: 'signup.wixel.title',
      },
      dividerTextKey: 'emailStep.wixel.or.login.with', // "or" divider between social and email
    },
    login: {
      title: {
        key: 'login.wixel.title',
      },
      subtitle: {
        key: 'login.wixel.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.wixel.primary_button',
        skin: 'standard',
        fullWidth: true,
      },
      alreadyHaveAccount: {
        title: {
          key: 'login.wixel.already_have_account',
        },
      },
      dividerTextKey: 'emailStep.wixel.or.login.with', // "or" divider between social and email
    },
    signup: {
      title: {
        key: 'signup.wixel.title',
      },
      subtitle: {
        key: 'signup.wixel.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.wixel.primary_button',
        skin: 'standard',
        fullWidth: true,
      },
      flows: [
        {
          id: 'image',
          title: {
            key: 'signup.wixel.image_flow.title',
          },
          subtitle: {
            key: 'signup.wixel.image_flow.subtitle',
          },
        },
      ],
      dividerTextKey: 'emailStep.wixel.signup.divider', // "or" divider between social and email
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      showArrowOnButton: true,
    },
    titleCustomFontSize: {
      authPage: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
      secondaryFlow: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
    },
    cssVariables: {
      '--wds-loader-fill-loading': '#D6FFE1',
      '--wds-loader-fill': '#1AAC40',
      '--wds-button-fill-standard-primary': '#442894',
      '--wds-button-fill-standard-primary-hover': '#442894',
      '--wds-button-fill-standard-primary-active': '#442894',
      '--wds-button-fill-standard-secondary-hover': '#442894',
      '--wds-button-fill-standard-secondary-active': '#442894',
      '--wds-color-text-primary': 'black',
      '--wds-font-weight-medium': '400',
      '--wds-font-weight-heading-1': '400',
      '--wds-font-weight-heading-2': '400',
      '--wds-font-weight-heading-3': '400',
      '--wds-color-border-standard-secondary': '#442894',
      '--wds-color-fill-standard-tertiary-hover': '#ffffff',
      '--wds-font-line-height-body-tiny': '18px',
      '--wds-font-family-display': 'Madefor Display',
    },
    background: `linear-gradient(144.34deg, rgba(205, 230, 255, 0.7) 31.48%, #8FF4AA 120.33%), linear-gradient(180deg, #ADD7FF 61.16%, #D3EAFF 100%)`,
    policies: {
      policyKey: 'policies_section.signup.wixel',
    },
    newGoogleSdkSocialButtonDesign: COMMON_NEW_GOOGLE_SDK_SOCIAL_BUTTON_DESIGN,
  },
  [PRESETS.PICASSO]: {
    id: PRESETS.PICASSO,
    shouldDisablePostLoginSuggestions: true,
    nameKey: 'presets.names.wixel',
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 52px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
    },
    logo: {
      src: 'https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/picasso-logo.png',
      url: 'https://www.wix.com/picasso',
      height: '90px',
      alt: 'Picasso',
      mobile: {
        height: '24px',
      },
    },
    landingPage: {
      title: {
        // `emailStep.picasso.title` was never added to Babel; reuse the existing,
        // fully-translated branded signup title ("Get started with Picasso") so
        // the landing screen shows branded copy instead of a raw key.
        key: 'signup.picasso.title',
      },
      dividerTextKey: 'emailStep.picasso.or.login.with', // "or" divider between social and email
    },
    login: {
      title: {
        key: 'login.picasso.title',
      },
      subtitle: {
        key: 'login.picasso.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.picasso.primary_button',
        skin: 'dark',
        fullWidth: true,
      },
      alreadyHaveAccount: {
        title: {
          key: 'login.picasso.already_have_account',
        },
      },
      dividerTextKey: 'emailStep.picasso.or.login.with', // "or" divider between social and email
    },
    signup: {
      title: {
        key: 'signup.picasso.title',
      },
      subtitle: {
        key: 'signup.picasso.subtitle',
      },
      primaryButtonDesign: {
        textKey: 'signup.picasso.primary_button',
        skin: 'dark',
        fullWidth: true,
      },
      flows: [
        {
          id: 'image',
          title: {
            key: 'signup.picasso.image_flow.title',
          },
          subtitle: {
            key: 'signup.picasso.image_flow.subtitle',
          },
        },
      ],
      dividerTextKey: 'emailStep.picasso.signup.divider', // "or" divider between social and email
    },
    buttonDesign: {
      skin: 'dark',
      fullWidth: true,
      showArrowOnButton: true,
    },
    cssVariables: {
      '--wds-loader-fill-loading': '#D6FFE1',
      '--wds-loader-fill': '#1AAC40',
    },
    background: `url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/logos/picasso-background.jpeg")`,
    // No `policies.policyKey`: `policies_section.signup.picasso` was never added
    // to Babel, so it rendered as a raw key. Falls back to the generic, translated
    // `policies_section.signup_content` (see PoliciesStore.expandedPolicyKey).
  },
  [PRESETS.WIX_2025]: {
    id: PRESETS.WIX_2025,
    shouldDisablePostLoginSuggestions: false,
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 48px 0',
        },
        borderRadius: '12px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        fullScreen: true, // Full-screen dialog on mobile (no card styling)
        primary: {
          padding: '12px 48px 36px 48px', // 48px horizontal = 24px card margin + 24px content padding from Figma
        },
        secondary: {
          padding: '12px 48px 36px 48px',
        },
        borderRadius: '0',
        boxShadow: 'none',
      },
    },
    logo: {
      height: '29px',
      mobile: {
        height: '18px',
      },
      dividerColor: '#F1F1F1', // Divider between Wix logo and account logo on SSO screens
    },
    socialButtonDesign: {
      borderColor: '#000000', // Black border per Figma (node 3808-12633)
      borderWidth: '1px', // Per Figma spec
      borderRadius: '6px', // Per Figma spec
      height: '43px',
      iconTextLayout: 'centered',
      fontSize: '14px',
    },
    newGoogleSdkSocialButtonDesign: {
      ...COMMON_NEW_GOOGLE_SDK_SOCIAL_BUTTON_DESIGN,
      height: '40px',
      borderRadius: '4px',
      iconTextLayout: 'centered',
      fontSize: '14px',
    },
    formSpacing: {
      inputButtonGap: '12px', // 12px gap between input and button when no forgot email link (Figma node 3808-12377)
      inputForgotEmailGap: '8px', // 8px gap between input and forgot email link when present
      forgotEmailButtonGap: '24px', // 24px gap between forgot email link and button
      policiesDividerSpacing: {
        top: '44px', // 44px above the horizontal divider (Figma node 3808-12646)
        centerVertically: true, // Center policies text between divider and bottom of card
      },
      titleSectionGap: '16px', // 16px gap between title and subtitle (Figma node 3808-12463)
      enterCodeButtonLayout: 'stacked', // Stacked full-width buttons in 2FA code dialogs (Figma node 3808-12663)
      secondaryDialogInputButtonGap: {
        desktop: '45px', // Total visual gap between input and button
      },
    },
    landingPage: {
      primaryButtonDesign: {
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true, // Hide ChevronRight icon to match Figma
      },
      dividerTextKey: 'emailStep.divider', // "or" divider between social and email
    },
    login: {
      primaryButtonDesign: {
        textKey: 'emailStep.submit', // "Continue with Email"
        skin: 'standard',
        fullWidth: true,
      },
      dividerTextKey: 'emailStep.divider', // "or" divider between social and email
    },
    signup: {
      primaryButtonDesign: {
        textKey: 'emailStep.submit', // "Continue with Email" for first step
        skin: 'standard',
        fullWidth: true,
      },
      emailFormButtonTextKey: 'signup.submit', // "Sign Up" for second step (password form)
      dividerTextKey: 'signup.divider', // "or" divider between social and email
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      height: '43px', // Figma spec from node 3808-12631
      fontSize: '14px', // Per Figma spec
      showArrowOnButton: false,
    },
    titleCustomFontSize: {
      authPage: {
        desktop: 'xLarge', // 36px with 16px margin-bottom (Figma node 3808-12631)
        mobile: 'xLarge', // 28px with 12px margin-bottom
      },
      secondaryFlow: {
        desktop: 'xLarge',
        mobile: 'xLarge',
      },
    },
    loaderColors: {
      fullPage: {
        fill: '#D5DFFF',
        fillLoading: '#2F5DFF',
      },
    },
    showCardLoader: true,
    cssVariables: {
      // Loader colors
      '--wds-loader-fill-loading': '#0F2CCF', // Primary spinner arc color
      '--wds-loader-fill': '#ffffff', // Background spinner arc color

      // Primary Button colors (Figma nodes: 3808-15760, 3808-16879, 3808-16886, 3808-16893)
      '--wds-button-fill-standard-primary': '#2f5dff', // Regular state (node 3808-15760)
      '--wds-button-fill-standard-primary-hover': '#1c3fe5', // Hover (node 3808-16879)
      '--wds-button-fill-standard-primary-active': '#0f2ccf', // Pressed (node 3808-16886)

      // Button border radius - override WDS default pill shape to rounded rectangle
      // Figma shows moderate border radius (node 3808-12628)
      '--wds-button-border-radius-medium': '6px',

      // Font family - Madefor Display for headings (Figma node 3808-12631)
      '--wds-font-family-display': 'Madefor Display',

      // Font weight - Medium weight for headings per 2025 spec
      '--wds-font-weight-heading-1': '500',

      // NOTE: These CSS variables follow the wixel preset pattern.
      // Additional button states (disabled, secondary) and input field colors
      // cannot be configured via CSS variables and require component-level SCSS overrides.
      // See docs/wix-2025-preset-notes.md for details on non-configurable styles.
    },
    // Switch link styling (Sign Up, Log In dialog switch links, Continue with SSO)
    switchLinkDesign: {
      color: '#2F5DFF',
      hoverColor: '#084EBD',
      focusRingColor: '#116DFF',
      underline: 'none',
      fontWeight: '500', // Medium
    },
    // Divider styling (between social buttons and email input)
    dividerDesign: {
      textColor: '#000000',
      lineColor: '#D4D4D4',
    },
    // Policies footer link styling
    policies: {
      linkColor: '#000624',
    },
    // Keep Brand theme enabled for input border colors
    useBrandTheme: true,
    // Override Brand theme's hardcoded secondary text color for policies/reCAPTCHA
    secondaryTextColor: '#42454C',
    // Background: responsive image-set with format fallbacks (AVIF → WebP → JPEG)
    // Browser automatically selects optimal format and resolution based on support and device pixel ratio
    background: `#f4f1f2 image-set(
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new%401x.avif") type("image/avif") 1x,
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new%402x.avif") type("image/avif") 2x,
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new%401x.webp") type("image/webp") 1x,
      url("https://wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new%402x.webp") type("image/webp") 2x,
      url("https://img-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new.png/v1/fit/w_2048,h_1410,q_90/wix-2025-new.jpeg") 1x,
      url("https://img-wixmp-7ef3383b5fd80a9f5a5cc686.wixmp.com/wix-2025-new/wix-2025-new.png/v1/fit/w_4096,h_2820,q_90/wix-2025-new.jpeg") 2x
    ) center center/2048px 1410px no-repeat`,
    backButtonPosition: 'aboveDialog',
    mobileBackButtonConfig: {
      position: 'header',
      marginTop: '23px',
      marginStart: '20px',
      iconTextGap: '4px',
    },
    // Dialog vertically centered with minimum top offset
    dialogVerticalPosition: 'centered',
    dialogMinTopOffset: '100px',
  },
  // Symphony — Wix's proactive "team of agents" product for SMBs.
  // Design source: the "🟢 Symphony brand - MKT" Figma, node 1:11185
  // (New sign up / Login page). The MKT design is a NEUTRAL take on the
  // Wix-2025 base — a solid BLACK primary CTA, blue Wix switch links, Archivo
  // display headings and Symphony-branded copy. It intentionally drops the
  // earlier green ("Signals") theming, so most brand fields simply inherit the
  // Wix-2025 base and only the deltas below are overridden.
  [PRESETS.SYMPHONY]: {
    id: PRESETS.SYMPHONY,
    // Branded name: `presets.names.symphony` = "Symphony". Drives the {brand}
    // placeholder on shared screens (e.g. 2FA) and the branded titles below.
    nameKey: 'presets.names.symphony',
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 48px 0',
        },
        // Card radius per Figma (24px vs the 12px Wix-2025 base).
        borderRadius: '24px',
        boxShadow: '0px 10px 30px 0px rgba(0, 0, 0, 0.15)',
      },
      mobile: {
        fullScreen: true, // Full-screen dialog on mobile (matches Wix-2025)
        primary: {
          padding: '12px 48px 36px 48px',
        },
        secondary: {
          padding: '12px 48px 36px 48px',
        },
        borderRadius: '0',
        boxShadow: 'none',
      },
    },
    logo: {
      // "Symphony by wix" lockup, monochrome charcoal — exported from the MKT
      // Figma (node 4517:12183) to match the neutral design; bundled locally,
      // same pattern as the other locally-served brand logos.
      src: symphonyLogo,
      height: '29px',
      mobile: {
        height: '22px',
      },
      alt: 'Symphony',
      url: 'https://www.wix.com/symphony',
    },
    // Symphony-branded titles + subtitles per the MKT Figma. Keys added to
    // Babel (mirrors the wixel precedent). The button/divider config is repeated
    // here because the preset merge replaces these objects wholesale (shallow),
    // so it must restate the Wix-2025 base values it wants to keep.
    landingPage: {
      title: {
        key: 'emailStep.symphony.title', // "Get started with Symphony"
      },
      primaryButtonDesign: {
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true, // Hide ChevronRight icon to match Figma
      },
      dividerTextKey: 'emailStep.divider', // "or" divider between social and email
    },
    login: {
      title: {
        key: 'login.symphony.title', // "Log in to Symphony"
      },
      subtitle: {
        key: 'login.symphony.subtitle', // "Enter your account info."
      },
      primaryButtonDesign: {
        textKey: 'emailStep.submit', // "Continue with Email"
        skin: 'standard',
        fullWidth: true,
      },
      dividerTextKey: 'emailStep.divider',
    },
    signup: {
      title: {
        key: 'signup.symphony.title', // "Get started with Symphony"
      },
      subtitle: {
        key: 'signup.symphony.subtitle', // "Create a Wix account to continue."
      },
      primaryButtonDesign: {
        textKey: 'emailStep.submit', // "Continue with Email" for first step
        skin: 'standard',
        fullWidth: true,
      },
      emailFormButtonTextKey: 'signup.submit', // "Sign Up" for the password step
      dividerTextKey: 'signup.divider',
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
      height: '43px', // Match Wix-2025 base
      fontSize: '14px',
      showArrowOnButton: false,
      // Primary Button label + disabled text + loading spinner per Figma.
      // Applied inline-scoped to the button in ButtonWithLoader (the underlying
      // WDS tokens are shared) so the full-page loader and unrelated text are
      // untouched.
      textColor: '#F3F3F3',
      disabledTextColor: '#B5B5B5',
      loaderColors: {
        // Loader "On Dark BG" spinner per Figma: track #5E5E5E, arc #FFFFFF.
        // (The Figma's "#0F2CCF" label is the annotation leader-dot color, not
        // the rendered track — pixel-sampling the actual spinner gives grey
        // ~#606060 ≈ #5E5E5E, matching the Button sheet.)
        fill: '#5E5E5E', // spinner track on the black button
        fillLoading: '#FFFFFF', // spinner arc
      },
    },
    // Restates the Wix-2025 form spacing (shallow merge replaces the whole
    // object) but tightens the title→subtitle gap to 6px per the Figma
    // (node itemSpacing) — the base uses 16px.
    formSpacing: {
      inputButtonGap: '12px',
      inputForgotEmailGap: '8px',
      forgotEmailButtonGap: '24px',
      policiesDividerSpacing: {
        top: '44px',
        centerVertically: true,
      },
      titleSectionGap: '6px', // Figma: tight gap between title and subtitle
      enterCodeButtonLayout: 'stacked',
      secondaryDialogInputButtonGap: {
        desktop: '45px',
      },
    },
    // Solid black primary CTA per Figma (overrides the Wix-2025 blue). Loader
    // and switch-link colors are intentionally NOT set so they inherit the
    // Wix-2025 blue via the merge below.
    cssVariables: {
      // Primary Button states per Figma (Interactive Components → Custom brand
      // Symphony). Fill: regular #000000, hover #333232, pressed/active #5E5E5E.
      '--wds-button-fill-standard-primary': '#000000',
      '--wds-button-fill-standard-primary-hover': '#333232',
      '--wds-button-fill-standard-primary-active': '#5E5E5E',
      // Border radius 8px. The WDS button root reads
      // --wds-button-border-radius-medium regardless of size="large" (theme
      // default is 6px); pin medium, plus large for safety.
      '--wds-button-border-radius-medium': '8px',
      '--wds-button-border-radius-large': '8px',
      // Disabled fill #EAE9E9 (button-only token — used solely by
      // ButtonCore--disabled, safe to set globally). The disabled TEXT (#B5B5B5)
      // is applied inline-scoped in ButtonWithLoader (see buttonDesign
      // .disabledTextColor) because its token is shared across components.
      '--wds-color-fill-standard-primary-disabled': '#EAE9E9',
      // Archivo display headings per Figma, with a Madefor Display fallback so
      // locales Archivo doesn't cover (Cyrillic/Hebrew/Arabic/CJK) still render
      // via per-glyph font fallback. Archivo is loaded in index.ejs/index.vm.
      '--wds-font-family-display': "'Archivo', 'Madefor Display'",
      // Archivo SemiBold (600) for the title, per Figma (wix-2025 base is 500).
      '--wds-font-weight-heading-1': '600',
    },
    // Full-page card loader in the Symphony brand color — BLACK, matching the
    // black CTA and the Figma "Animated Components → Custom brand (Symphony)"
    // loader (#000000 vs the #3B6BE1 default WDS). Overrides the inherited
    // wix-2025 blue loader.
    loaderColors: {
      fullPage: {
        // Loader on light BG per Figma: track #DDDDDD, moving arc #000000.
        fill: '#DDDDDD', // track
        fillLoading: '#000000', // moving arc
      },
    },
    // Social buttons above the email form on the login / email-step screens,
    // matching the signup screen and the MKT Figma login layout.
    socialFirst: true,
    // Social Button states per Figma (Custom brand Symphony). Neutral outlined
    // button: white fill, grey border, black label — with grey hover/pressed
    // fills, a red error border, and a light-track/dark-arc spinner. Consumed
    // via display.resolvedSocialButtonDesign → SocialLoginButton (.newDesign).
    socialButtonDesign: {
      height: '43px', // H43
      borderColor: '#969696', // regular border 1px
      borderWidth: '1px',
      borderRadius: '8px',
      // NOTE: intentionally no `backgroundColor` — the .newDesign base is
      // already #fff, and setting it inline would override the :hover/:active
      // background rules (inline styles beat pseudo-class rules).
      textColor: '#000000',
      hoverBackgroundColor: '#EAE9E9',
      hoverBorderColor: '#B5B5B5',
      activeBackgroundColor: '#DDDDDD',
      activeBorderColor: '#B5B5B5',
      disabledBackgroundColor: '#EAE9E9',
      disabledTextColor: '#B5B5B5',
      errorBorderColor: '#DF3336',
      loaderFill: '#F3F3F3', // spinner track
      loaderFillLoading: '#000000', // spinner arc
      focusOutline: '1.5px solid #000000', // black tab-focus ring per Figma
      focusOutlineOffset: '1px',
    },
    // "or" divider between social buttons and email input — line per Figma.
    dividerDesign: {
      textColor: '#000000',
      lineColor: '#EAE9E9',
    },
    // Symphony-specific legal copy per Figma: "By signing up to Symphony, you'll
    // create a Wix account and agree to our Terms of Use…" (policyKey drives the
    // expanded policies sentence — see PoliciesStore.expandedPolicyKey). linkColor
    // restated because the shallow preset merge replaces the whole `policies`.
    policies: {
      policyKey: 'policies_section.symphony.signup_content',
      linkColor: '#000624',
      minimalFitContent: true,
    },
    // Subtitle typography per Figma (Madefor Text 22px / 21px line-height) —
    // the Wix-2025 base renders the preset subtitle at the smaller WDS "small"
    // size. Applied to the subtitle element in AuthPage, gated to this preset.
    subtitleFontSize: {
      // Subheader per Figma (Wix Madefor Text, Regular): 22px desktop / 18px
      // mobile, line-height 21, letter-spacing -1%.
      fontSize: '22px',
      lineHeight: '21px',
      mobileFontSize: '18px',
      letterSpacing: '-0.01em',
    },
    // Symphony marketing background — official left/right avatar+stat element
    // strips pinned to each edge with a side-fade overlay. Applied via a CSS
    // class (see App.scss `.symphonyBackground`) rather than an inline
    // `background` so the bundled WebP/PNG assets are referenced from SCSS
    // (webpack-resolved) and never imported into JS — keeping them out of the
    // jest module graph. The strips stay on the sides at every desktop width.
    backgroundClassName: 'symphonyBackground',
    // Back button in the header, below the logo (left-aligned) per Figma —
    // rather than the Wix-2025 'aboveDialog' placement.
    backButtonPosition: 'header',
    // Back button per Figma: chevron icon (‹) + semibold label, 16px desktop /
    // 14px mobile (default is an arrow + normal weight). Consumed in MainHeader.
    backButtonDesign: {
      chevronIcon: true,
      fontWeight: '600',
      fontSize: '16px',
      mobileFontSize: '14px',
    },
  },

  [PRESETS.VIBU]: {
    id: PRESETS.VIBU,
    // Preset for the standalone "vibu" product (vibu.dev / manage.wix.com/vibu).
    // Same modern vertical layout as wix-2025 / Symphony, in vibu's actual brandbook
    // (pink/blue/lime, Nunito) — see docs/BRAND.md + Vibu.pdf in vibu-app-landing.
    // Copy uses only generic keys that exist in Babel (no vibu-specific translation
    // keys yet) so nothing renders as a raw key — same approach as Symphony.
    container: {
      layout: 'vertical',
      desktop: {
        primary: {
          padding: '48px 0 24px 0',
        },
        secondary: {
          padding: '48px 0 48px 0',
        },
        borderRadius: '16px',
        boxShadow: '0px 10px 30px 0px rgba(255, 31, 109, 0.15)',
      },
      mobile: {
        primary: {
          padding: '36px 0 54px 0',
        },
        secondary: {
          padding: '36px 0 54px 0',
        },
        borderRadius: '16px',
        boxShadow: '0px 10px 30px 0px rgba(255, 31, 109, 0.15)',
      },
    },
    logo: {
      src: vibuLogo,
      height: '32px',
      mobile: {
        height: '26px',
      },
      alt: 'Vibu',
      url: 'https://vibu.dev',
    },
    landingPage: {
      // Filled, full-width pink primary CTA
      primaryButtonDesign: {
        skin: 'standard',
        fullWidth: true,
        hideSuffixIcon: true,
      },
      dividerTextKey: 'emailStep.divider', // generic "or" key (exists in Babel)
    },
    login: {
      primaryButtonDesign: {
        textKey: 'emailStep.submit',
        skin: 'standard',
        fullWidth: true,
      },
      dividerTextKey: 'emailStep.divider',
    },
    signup: {
      title: {
        key: 'signup.title', // generic key, exists in Babel
      },
      primaryButtonDesign: {
        textKey: 'emailStep.submit',
        skin: 'standard',
        fullWidth: true,
      },
      emailFormButtonTextKey: 'signup.submit',
      dividerTextKey: 'signup.divider',
    },
    buttonDesign: {
      skin: 'standard',
      fullWidth: true,
    },
    // vibu palette (brandbook Color System): Pink 500 primary CTA + Pink/Lime loader,
    // Nunito for headings/buttons (Fraunces is reserved for hero/stat display type).
    cssVariables: {
      '--wds-button-fill-standard-primary': '#ff1f6d',
      '--wds-button-fill-standard-primary-hover': '#7a0047',
      '--wds-button-fill-standard-primary-active': '#300018',
      '--wds-loader-fill-loading': '#ff1f6d',
      '--wds-loader-fill': '#ebff89',
      '--wds-font-family-display': 'Nunito',
    },
    // Switch links (Sign Up / Log In / Continue with SSO) in vibu pink
    switchLinkDesign: {
      color: '#ff1f6d',
      hoverColor: '#7a0047',
      focusRingColor: '#ff1f6d',
      underline: 'none',
      fontWeight: '500',
    },
    // "or" divider between social buttons and email input
    dividerDesign: {
      textColor: '#171434',
      lineColor: '#e7e9f3',
    },
    // Policies footer links in vibu pink
    policies: {
      linkColor: '#ff1f6d',
    },
    loaderColors: {
      fullPage: {
        fill: '#ebff89',
        fillLoading: '#ff1f6d',
      },
    },
    // vibu brand background — the same pink/blue/cream mesh gradient as the
    // vibu.dev landing page's `.mesh-bg` utility (src/styles.css).
    background: `radial-gradient(58% 52% at 12% 6%, rgba(120, 140, 246, 0.5) 0%, transparent 58%), radial-gradient(52% 52% at 92% 4%, rgba(244, 120, 186, 0.52) 0%, transparent 56%), radial-gradient(72% 60% at 50% 112%, rgba(253, 240, 176, 0.7) 0%, transparent 62%), radial-gradient(46% 40% at 50% 42%, rgba(255, 255, 255, 0.55) 0%, transparent 72%), linear-gradient(180deg, #eef1fb 0%, #fdfcf6 100%)`,
  },
};

// Symphony inherits the Wix-2025 preset as its base, then layers its own brand
// identity on top. From Wix-2025 it picks up everything it doesn't define itself:
// social-button styling, form spacing, full-page card loader + blue loader
// colors, blue switch links, blue policy links, title font sizes, secondary text
// color, Brand-theme flag, dialog centering and back-button placement.
// Symphony's own fields (nameKey, logo, container radius, branded title/subtitle
// keys, black CTA, Archivo font, Figma divider line, marketing background) win.
// cssVariables are deep-merged so the black CTA + Archivo font override while
// Wix-2025's structural vars (button border radius, heading font-weight, blue
// loader) are still inherited.
ViewPresets[PRESETS.SYMPHONY] = {
  ...ViewPresets[PRESETS.WIX_2025],
  ...ViewPresets[PRESETS.SYMPHONY],
  cssVariables: {
    ...ViewPresets[PRESETS.WIX_2025].cssVariables,
    ...ViewPresets[PRESETS.SYMPHONY].cssVariables,
  },
};

// vibu inherits the Wix-2025 base the same way Symphony does: it picks up
// everything it doesn't define (social-button styling, form spacing, title
// font sizes, dialog centering, generic translated title/subtitle keys, etc.),
// while vibu's own fields (logo, container, pink CTA/links/divider/policies,
// loaderColors, background) win. cssVariables are shallow-merged one level
// deep so vibu's brand colors override but Wix-2025's structural vars are
// kept.
ViewPresets[PRESETS.VIBU] = {
  ...ViewPresets[PRESETS.WIX_2025],
  ...ViewPresets[PRESETS.VIBU],
  cssVariables: {
    ...ViewPresets[PRESETS.WIX_2025].cssVariables,
    ...ViewPresets[PRESETS.VIBU].cssVariables,
  },
};
